<template>
  <div
    class="portfolio-instructions-display d-flex align-items-center p-3 border rounded align-items-center"
  >
    <div class="me-3">
      <div
        :class="[
          'portfolio-icon',
          portfolioStyling.iconBgClass,
          'bg-body rounded-circle d-flex align-items-center justify-content-center border border-1 flex-shrink-0',
        ]"
      >
        <FontAwesomeIcon
          :icon="portfolioStyling.icon"
          :class="portfolioStyling.iconTextClass"
        />
      </div>
    </div>
    <div class="flex-grow-1">
      <h5 class="mb-1">
        {{ portfolioName }}
      </h5>
      <div class="d-flex flex-wrap gap-2">
        <span
          v-for="badge in displayBadges"
          :key="badge.key"
          class="badge badge-phoenix badge-phoenix-secondary fs-10"
        >
          <FontAwesomeIcon :icon="badge.icon" :class="badge.iconClass" />
          {{ badge.text }}
        </span>
      </div>
    </div>
    <div class="ms-auto">
      <slot name="edit-button"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  findLimitAt,
  formatPercentageDisplay,
  periodLabelForLimit,
} from "@/dashboard/production_strategy/overview/PPA/ppaPortfolioUtils";
import type { PortfolioWithInstructions, RegistryDevice } from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  portfolio: PortfolioWithInstructions;
  registryDevices?: RegistryDevice[]; // Used for rendering device list if applicable
}>();

const { t } = useI18n();

/**
 * Get the badge text for the limit covering today.
 * Works for yearly, quarterly, and monthly granularities.
 */
function getCurrentPeriodLimitBadgeText(): string {
  const limits = props.portfolio.portfolio_instructions.limits;
  if (limits.length === 0) {
    return t("portfolio_instructions.allocate_fixed_amount");
  }

  const current = findLimitAt(limits, new Date());
  if (!current) {
    return t("portfolio_instructions.allocate_fixed_amount");
  }

  return t("portfolio_instructions.current_period_limit", {
    amount: current.limit_mwh,
    unit: t("common.mwh"),
    periodLabel: periodLabelForLimit(current),
  });
}

/**
 * Determines the icon and styling based on portfolio type
 */
const portfolioStyling = computed(() => {
  switch (props.portfolio.portfolio_type) {
    case "PPA":
      return {
        icon: "fas fa-file-contract",
        iconBgClass: "bg-primary-subtle",
        iconTextClass: "text-primary",
      };
    case "HOLD":
      return {
        icon: "fas fa-warehouse",
        iconBgClass: "bg-warning-subtle",
        iconTextClass: "text-warning",
      };
    case "SPOT_MARKET_PRICE":
      return {
        icon: "fas fa-gavel",
        iconBgClass: "bg-success-subtle",
        iconTextClass: "text-success",
      };
    case "SPOT_MIN_PRICE":
      return {
        icon: "fas fa-gavel",
        iconBgClass: "bg-success-subtle",
        iconTextClass: "text-success",
      };
    default:
      return {
        icon: "fas fa-briefcase",
        iconBgClass: "bg-secondary-subtle",
        iconTextClass: "text-secondary",
      };
  }
});

const portfolioName = computed(() => {
  if (props.portfolio.portfolio_type === "HOLD") {
    return t("hold_strategy.hold_portfolio");
  }
  return props.portfolio.name;
});

const devicesBadgeText = computed(() => {
  const { registry_device_ids } = props.portfolio.portfolio_instructions;

  if (registry_device_ids.length === 0) {
    return "";
  }

  if (props.registryDevices && props.registryDevices.length > 0) {
    const deviceNames = registry_device_ids
      .map((id: string) => {
        const device = props.registryDevices?.find(
          (d) => d.id.toString() === id
        );
        return device ? device.device_name : id;
      })
      .join(", ");
    return deviceNames;
  } else {
    return t("portfolio_instructions.devices_count_display", {
      count: registry_device_ids.length,
    });
  }
});

const displayBadges = computed(() => {
  const badges = [];

  if (props.portfolio.portfolio_instructions.allocate_percentage) {
    if (props.portfolio.portfolio_instructions.limits.length > 0) {
      badges.push({
        key: "limits",
        icon: "fas fa-arrows-up-to-line",
        iconClass: "me-1",
        text: getCurrentPeriodLimitBadgeText(),
      });
    } else {
      badges.push({
        key: "allocate_percentage",
        icon: "fas fa-chart-pie",
        iconClass: "me-1",
        text: t("portfolio_instructions.allocate_percentage_display", {
          percent: formatPercentageDisplay(
            props.portfolio.portfolio_instructions.allocate_percentage
          ),
        }),
      });
    }
  } else {
    badges.push({
      key: "allocate_percentage",
      icon: "fas fa-chart-pie",
      iconClass: "me-1",
      text: t("portfolio_instructions.allocate_percentage_display", {
        percent: formatPercentageDisplay(0),
      }),
    });
  }

  if (props.portfolio.portfolio_instructions.registry_device_ids.length > 0) {
    badges.push({
      key: "devices",
      icon: "fas fa-plug",
      iconClass: "me-1",
      text: devicesBadgeText.value,
    });
  } else {
    badges.push({
      key: "from_all",
      icon: "fas fa-arrows-maximize",
      iconClass: "me-1",
      text: t("portfolio_instructions.from_all"),
    });
  }

  if (props.portfolio.custom_user_upload_id) {
    badges.push({
      key: "custom_user_upload",
      icon: "fas fa-paperclip",
      iconClass: "",
      text: "",
    });
  }

  return badges;
});
</script>

<style scoped lang="scss">
.portfolio-icon {
  width: 2rem;
  height: 2rem;
  z-index: 2;
}
</style>
